import React, { useEffect, useState } from "react";
import {
  Box, Paper, Typography, Tabs, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Chip, CircularProgress,
  Snackbar, Alert, Tooltip as MuiTooltip, IconButton
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import StoreIcon from "@mui/icons-material/Store";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import BlockIcon from "@mui/icons-material/Block";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from "recharts";
import {
  getWhatsAppDashboard, getWhatsAppResellers, createWhatsAppReseller,
  getWhatsAppClients, approveWhatsAppClient, suspendWhatsAppClient, reactivateWhatsAppClient,
  deletePendingVerification
} from "../api/adminApi";
import StatCard from "../components/StatCard";
import ChartCard from "../components/ChartCard";
import ReplaceWABADialog from "../components/ReplaceWABADialog";

const statusColors = {
  not_started: "#64748b", fb_connected: "#f59e0b", pending: "#f59e0b",
  payment_pending: "#f59e0b", onboarding: "#3b82f6", details_submitted: "#8b5cf6",
  otp_ready: "#3b82f6", otp_verified: "#22c55e", active: "#22c55e", suspended: "#ef4444",
  blocked: "#ef4444", unverified: "#f59e0b", api_error: "#f97316", no_token: "#f59e0b"
};

const statusLabels = {
  not_started: "Not Started", fb_connected: "FB Connected", pending: "Pending",
  payment_pending: "Payment Needed", onboarding: "Setting Up", details_submitted: "Details Submitted",
  otp_ready: "OTP Ready", otp_verified: "OTP Verified", active: "Active", suspended: "Suspended",
  blocked: "Blocked", unverified: "Unverified", api_error: "API Error", no_token: "No Token"
};

const hourLabel = (h) => {
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h % 12 || 12;
  return `${hr} ${ampm}`;
};

const statusChip = (c) => {
  if (c.apiStatus === "blocked") return { label: statusLabels.blocked, color: statusColors.blocked, title: c.apiStatusDetail };
  if (c.apiStatus === "unverified") return { label: statusLabels.unverified, color: statusColors.unverified, title: c.apiStatusDetail };
  if (c.apiStatus === "api_error") return { label: statusLabels.api_error, color: statusColors.api_error, title: c.apiStatusDetail };
  if (c.apiStatus === "no_token") return { label: statusLabels.no_token, color: statusColors.no_token, title: c.apiStatusDetail };
  const os = c.whatsapp?.onboardingStatus || "not_started";
  if (os === "active") {
    if (c.apiStatus === "active") return { label: statusLabels.active, color: statusColors.active, title: c.apiStatusDetail || "" };
    if (!c.whatsapp?.phoneNumberId || !c.whatsapp?.wabaId) {
      return { label: "Needs New WABA", color: "#ef4444", title: "Phone number / WABA missing — use Replace WABA" };
    }
  }
  return { label: statusLabels[os] || os, color: statusColors[os] || "#64748b", title: "" };
};

const isReplaceable = (c) => {
  const wa = c.whatsapp;
  if (!wa || wa.onboardingStatus !== "active") return false;
  if (c.apiStatus === "active") return false;
  if (c.apiStatus === "blocked" || c.apiStatus === "api_error" || c.apiStatus === "unverified" || c.apiStatus === "no_token") return true;
  if (!wa.phoneNumberId || !wa.wabaId) return true;
  return false;
};

const WhatsApp = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [data, setData] = useState({ stats: { totalResellers: 0, totalClients: 0, activeAccounts: 0, pendingAccounts: 0, blockedAccounts: 0, apiErrorAccounts: 0 }, resellers: [], pendingVerifications: [], chatActivity: { days: [], hours: [], totalMessages: 0, totalChats: 0, peakHour: -1, peakCount: 0 } });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ fullName: "", email: "", password: "" });
  const [verifyDialog, setVerifyDialog] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [clientsRefreshing, setClientsRefreshing] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = async () => {
    try {
      const d = await getWhatsAppDashboard();
      setData(d);
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    }
  };

  const fetchClients = async () => {
    setClientsRefreshing(true);
    try {
      const c = await getWhatsAppClients();
      setClients(c.clients || []);
      setLastChecked(new Date());
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    }
    setClientsRefreshing(false);
  };

  useEffect(() => {
    if (tab !== 1) return;
    const t = setInterval(() => { fetchClients(); }, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [tab]);

  useEffect(() => {
    Promise.all([fetchData(), fetchClients()]).finally(() => setLoading(false));
  }, []);

  const handleCreateReseller = async () => {
    try {
      await createWhatsAppReseller(form);
      setOpen(false);
      setForm({ fullName: "", email: "", password: "" });
      await fetchData();
      setSnack({ open: true, message: "Reseller created", severity: "success" });
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    }
  };

  const handleApprove = async (clientId) => {
    try {
      await approveWhatsAppClient(clientId);
      await fetchData();
      setSnack({ open: true, message: "Client approved for payment", severity: "success" });
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    }
  };

  const handleDeletePending = async () => {
    if (!deleteDialog) return;
    setDeleting(true);
    try {
      await deletePendingVerification(deleteDialog._id);
      setSnack({ open: true, message: "Pending verification record deleted", severity: "success" });
      setDeleteDialog(null);
      await fetchData();
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    }
    setDeleting(false);
  };

  const handleSuspend = async (clientId) => {
    if (!window.confirm("Suspend this client? The client will lose WhatsApp access until reactivated.")) return;
    try {
      await suspendWhatsAppClient(clientId);
      await Promise.all([fetchData(), fetchClients()]);
      setSnack({ open: true, message: "Client suspended", severity: "success" });
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    }
  };

  const handleReactivate = async (clientId) => {
    if (!window.confirm("Reactivate this client? WhatsApp access will be restored.")) return;
    try {
      await reactivateWhatsAppClient(clientId);
      await Promise.all([fetchData(), fetchClients()]);
      setSnack({ open: true, message: "Client reactivated", severity: "success" });
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    }
  };



  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;

  const { stats, resellers, pendingVerifications, chatActivity = { days: [], hours: [], totalMessages: 0, totalChats: 0, peakHour: -1, peakCount: 0 } } = data;

  return (
    <Box>
      <Box>
        <Typography fontWeight={800} fontSize={24} color="#1e293b" mb={0.5}>WhatsApp Platform</Typography>
        <Typography variant="body2" color="#64748b" mb={3}>Manage resellers, clients & verifications</Typography>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: "27px", mb: 5 }}>
        <StatCard icon={<StoreIcon sx={{ color: "#6366f1", fontSize: 24 }} />} label="Resellers" value={stats.totalResellers} color="#6366f1" />
        <StatCard icon={<PeopleIcon sx={{ color: "#3b82f6", fontSize: 24 }} />} label="Total Clients" value={stats.totalClients} color="#3b82f6" />
        <StatCard icon={<CheckCircleIcon sx={{ color: "#22c55e", fontSize: 24 }} />} label="Active" value={stats.activeAccounts} color="#22c55e" />
        <StatCard icon={<HourglassEmptyIcon sx={{ color: "#f59e0b", fontSize: 24 }} />} label="Pending" value={stats.pendingAccounts} color="#f59e0b" />
        <StatCard icon={<BlockIcon sx={{ color: "#ef4444", fontSize: 24 }} />} label="Blocked by Meta" value={stats.blockedAccounts} color="#ef4444" />
        <StatCard icon={<ErrorOutlineIcon sx={{ color: "#f97316", fontSize: 24 }} />} label="API Error" value={stats.apiErrorAccounts} color="#f97316" />
        <StatCard icon={<WarningAmberIcon sx={{ color: "#f59e0b", fontSize: 24 }} />} label="Unverified" value={stats.unverifiedAccounts || 0} color="#f59e0b" />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "27px", mb: 5 }}>
        <ChartCard title="Chat Activity" subtitle={`Messages across all WhatsApp businesses · last 30 days (${chatActivity.totalChats} chats, ${chatActivity.totalMessages} msgs)`} height={280}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chatActivity.days} barCategoryGap="15%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#64748b" }} axisLine={false} tickLine={false}
                tickFormatter={(v) => (v ? v.slice(5).replace("-", "/") : "")} minTickGap={24} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} width={32} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)", fontSize: 13 }}
                labelFormatter={(v) => `${v} · ${chatActivity.days.find((d) => d.date === v)?.count || 0} messages`} />
              <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Peak Hours" subtitle={chatActivity.peakHour >= 0 ? `Peak activity: ${hourLabel(chatActivity.peakHour)} IST (${chatActivity.peakCount} msgs)` : "Messages by hour of day (IST)"} height={280}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chatActivity.hours} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="hour" tick={{ fontSize: 9, fill: "#64748b" }} axisLine={false} tickLine={false}
                tickFormatter={(h) => hourLabel(h)} interval={2} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} width={32} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)", fontSize: 13 }}
                labelFormatter={(h) => `${hourLabel(h)} IST · ${chatActivity.hours.find((x) => x.hour === h)?.count || 0} messages`} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {chatActivity.hours.map((h) => (
                  <Cell key={h.hour} fill={h.hour === chatActivity.peakHour ? "#6366f1" : "#c7d2fe"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Box>

      <Paper className="glass-card" sx={{ overflow: "hidden" }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: "1px solid rgba(255,255,255,0.5)", "& .MuiTab-root": { color: "#64748b", textTransform: "none", fontWeight: 600 }, "& .Mui-selected": { color: "#6366f1" }, "& .MuiTabs-indicator": { bgcolor: "#6366f1" } }}>
          <Tab label="Clients" />
          <Tab label="Resellers" />
          <Tab label="Pending Verifications" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="caption" color="#94a3b8">
                {lastChecked ? `Last checked: ${lastChecked.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} (auto-refresh every 60s)` : "Checking live Meta status..."}
              </Typography>
              <Button size="small" variant="outlined" onClick={fetchClients} disabled={clientsRefreshing}
                sx={{ color: "#6366f1", borderColor: "#6366f1", textTransform: "none", borderRadius: 1.5, fontSize: 12 }}>
                {clientsRefreshing ? <CircularProgress size={14} sx={{ color: "#6366f1", mr: 0.5 }} /> : null}
                {clientsRefreshing ? "Checking..." : "Refresh Status"}
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Name</TableCell>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Email</TableCell>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Status</TableCell>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Business</TableCell>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Dashboard</TableCell>
                      <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                  {clients.map((c) => {
                    const wa = c.whatsapp;
                    const chip = statusChip(c);
                    return (
                      <TableRow key={c._id}>
                        <TableCell sx={{ color: "#1e293b", borderColor: "#e2e8f0" }}>{c.fullName}</TableCell>
                        <TableCell sx={{ color: "#475569", borderColor: "#e2e8f0" }}>{c.email}</TableCell>
                        <TableCell sx={{ borderColor: "#e2e8f0" }}>
                          <MuiTooltip title={chip.title || ""} arrow>
                            <Chip label={chip.label} size="small" sx={{ color: "#fff", bgcolor: chip.color, fontWeight: 600, fontSize: 12 }} />
                          </MuiTooltip>
                        </TableCell>
                        <TableCell sx={{ color: "#475569", borderColor: "#e2e8f0" }}>{wa?.businessName || wa?.companyName || "—"}</TableCell>
                        <TableCell sx={{ borderColor: "#e2e8f0" }}>
                          <Button size="small" variant="text" onClick={() => navigate(`/whatsapp/client/${c._id}`)} sx={{ color: "#6366f1", textTransform: "none", borderRadius: 1.5, fontSize: 12, fontWeight: 600 }}>
                            Dashboard
                          </Button>
                        </TableCell>
                        <TableCell sx={{ borderColor: "#e2e8f0" }}>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {isReplaceable(c) && (
                              <Button size="small" variant="contained" onClick={() => setVerifyDialog(c)} sx={{ bgcolor: "#ef4444", "&:hover": { bgcolor: "#dc2626" }, textTransform: "none", borderRadius: 1.5, fontSize: 12 }}>
                                Replace WABA
                              </Button>
                            )}
                            {wa?.onboardingStatus === "pending" && (
                              <Button size="small" variant="contained" onClick={() => handleApprove(c._id)} sx={{ bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" }, textTransform: "none", borderRadius: 1.5, fontSize: 12 }}>
                                Approve
                              </Button>
                            )}
                            {wa?.onboardingStatus === "otp_verified" && (
                              <Button size="small" variant="contained" onClick={() => setVerifyDialog(c._id)} sx={{ bgcolor: "#22c55e", "&:hover": { bgcolor: "#16a34a" }, textTransform: "none", borderRadius: 1.5, fontSize: 12 }}>
                                Verify
                              </Button>
                            )}
                            {wa?.onboardingStatus === "active" && (
                              <Button size="small" variant="outlined" onClick={() => handleSuspend(c._id)} sx={{ color: "#ef4444", borderColor: "#ef4444", textTransform: "none", borderRadius: 1.5, fontSize: 12, "&:hover": { borderColor: "#dc2626", bgcolor: "#fef2f2" } }}>
                                Suspend
                              </Button>
                            )}
                            {wa?.onboardingStatus === "suspended" && (
                              <Button size="small" variant="outlined" onClick={() => handleReactivate(c._id)} sx={{ color: "#22c55e", borderColor: "#22c55e", textTransform: "none", borderRadius: 1.5, fontSize: 12, "&:hover": { borderColor: "#16a34a", bgcolor: "#f0fdf4" } }}>
                                Reactivate
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {clients.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ color: "#64748b", textAlign: "center", borderColor: "#e2e8f0" }}>No clients yet</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Button variant="contained" onClick={() => setOpen(true)} sx={{ bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" }, textTransform: "none", borderRadius: 2 }}>
                Add Reseller
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Name</TableCell>
                    <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Email</TableCell>
                    <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Clients</TableCell>
                    <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Joined</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resellers.map((r) => (
                    <TableRow key={r._id}>
                      <TableCell sx={{ color: "#1e293b", borderColor: "#e2e8f0" }}>{r.fullName}</TableCell>
                      <TableCell sx={{ color: "#475569", borderColor: "#e2e8f0" }}>{r.email}</TableCell>
                      <TableCell sx={{ color: "#1e293b", borderColor: "#e2e8f0" }}>{r.clientCount || 0}</TableCell>
                      <TableCell sx={{ color: "#475569", borderColor: "#e2e8f0" }}>{new Date(r.createdAt).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                  {resellers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ color: "#64748b", textAlign: "center", borderColor: "#e2e8f0" }}>No resellers yet</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ p: 2 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Client</TableCell>
                    <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Email</TableCell>
                    <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Business</TableCell>
                    <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Submitted</TableCell>
                    <TableCell sx={{ color: "#64748b", fontWeight: 600, borderColor: "#e2e8f0" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingVerifications.map((pv) => (
                    <TableRow key={pv._id}>
                      <TableCell sx={{ color: "#1e293b", borderColor: "#e2e8f0" }}>{pv.userId?.fullName || pv.fullName || "—"}</TableCell>
                      <TableCell sx={{ color: "#475569", borderColor: "#e2e8f0" }}>{pv.userId?.email || pv.email || "—"}</TableCell>
                      <TableCell sx={{ color: "#1e293b", borderColor: "#e2e8f0" }}>{pv.businessName || "—"}</TableCell>
                      <TableCell sx={{ color: "#475569", borderColor: "#e2e8f0" }}>{new Date(pv.updatedAt).toLocaleDateString()}</TableCell>
                      <TableCell sx={{ borderColor: "#e2e8f0" }}>
                        {pv.onboardingStatus === "pending" && (
                          <Button size="small" variant="contained" onClick={() => handleApprove(pv.userId?._id)} sx={{ bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" }, textTransform: "none", borderRadius: 1.5, fontSize: 12 }}>
                            Approve
                          </Button>
                        )}
                        {pv.onboardingStatus === "otp_verified" && (
                          <Button size="small" variant="contained" onClick={() => setVerifyDialog(pv.userId?._id)} sx={{ bgcolor: "#22c55e", "&:hover": { bgcolor: "#16a34a" }, textTransform: "none", borderRadius: 1.5, fontSize: 12 }}>
                            Verify
                          </Button>
                        )}
                        {pv.onboardingStatus === "payment_pending" && (
                          <Chip label="Awaiting Payment" size="small" sx={{ color: "#fff", bgcolor: "#f59e0b", fontWeight: 600, fontSize: 12 }} />
                        )}
                        {pv.onboardingStatus === "onboarding" && (
                          <Chip label="Filling Details" size="small" sx={{ color: "#fff", bgcolor: "#3b82f6", fontWeight: 600, fontSize: 12 }} />
                        )}
                        {pv.onboardingStatus === "details_submitted" && (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Chip label="Details Submitted" size="small" sx={{ color: "#fff", bgcolor: "#8b5cf6", fontWeight: 600, fontSize: 12 }} />
                            <Button size="small" variant="contained" onClick={() => window.open(`/users/${pv.userId?._id}`, '_blank')}
                              sx={{ bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" }, textTransform: "none", borderRadius: 1.5, fontSize: 12 }}>
                              Configure
                            </Button>
                          </Box>
                        )}
                        {pv.onboardingStatus === "otp_ready" && (
                          <Chip label="OTP Ready" size="small" sx={{ color: "#fff", bgcolor: "#22c55e", fontWeight: 600, fontSize: 12 }} />
                        )}
                        <MuiTooltip title="Delete this pending verification record (testing / stale data)">
                          <IconButton size="small" onClick={() => setDeleteDialog(pv)} sx={{ color: "#ef4444", ml: 1 }}>
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </MuiTooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {pendingVerifications.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ color: "#64748b", textAlign: "center", borderColor: "#e2e8f0" }}>No pending verifications</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ className: "glass-card", sx: { borderRadius: "16px !important" } }}>
        <DialogTitle sx={{ color: "#1e293b", fontWeight: 700 }}>Add Reseller</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField label="Full Name" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} fullWidth size="small" sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#ffffff", color: "#1e293b", "& fieldset": { borderColor: "#e2e8f0" } }, "& .MuiInputLabel-root": { color: "#64748b" } }} />
            <TextField label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} fullWidth size="small" sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#ffffff", color: "#1e293b", "& fieldset": { borderColor: "#e2e8f0" } }, "& .MuiInputLabel-root": { color: "#64748b" } }} />
            <TextField label="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} fullWidth size="small" helperText="Default: Reseller@123" sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#ffffff", color: "#1e293b", "& fieldset": { borderColor: "#e2e8f0" } }, "& .MuiInputLabel-root": { color: "#64748b" }, "& .MuiFormHelperText-root": { color: "#64748b" } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: "#64748b", textTransform: "none" }}>Cancel</Button>
          <Button onClick={handleCreateReseller} variant="contained" sx={{ bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" }, textTransform: "none", borderRadius: 2 }}>Create</Button>
        </DialogActions>
      </Dialog>

      <ReplaceWABADialog
        open={!!verifyDialog}
        client={verifyDialog}
        onClose={() => setVerifyDialog(null)}
        onSuccess={() => { setVerifyDialog(null); fetchData(); fetchClients(); }}
      />

      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} maxWidth="xs" fullWidth PaperProps={{ className: "glass-card", sx: { borderRadius: "16px !important" } }}>
        <DialogTitle sx={{ color: "#1e293b", fontWeight: 700 }}>Delete Pending Verification?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#475569" }}>
            This deletes the onboarding record for{" "}
            <b>{deleteDialog?.businessName || deleteDialog?.companyName || deleteDialog?.userId?.fullName || deleteDialog?._id}</b>.
            The client user account will NOT be deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setDeleteDialog(null)} sx={{ color: "#64748b", textTransform: "none" }}>Cancel</Button>
          <Button onClick={handleDeletePending} disabled={deleting} variant="contained" sx={{ bgcolor: "#ef4444", "&:hover": { bgcolor: "#dc2626" }, textTransform: "none", borderRadius: 2 }}>
            {deleting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: "100%", borderRadius: 2 }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsApp;
